import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { usersApi } from '@/lib/api-client';
import {
  getPushSubscription,
  isPushNotificationSupported,
  requestNotificationPermission,
  serializePushSubscription,
  subscribeToPushNotifications,
} from '@/utils/push-notifications';

interface UseAutoPushSubscriptionOptions {
  /**
   * Whether to request permission if it's 'default' (user hasn't made a choice)
   * @default false
   */
  requestPermissionIfDefault?: boolean;
}

/**
 * Automatically subscribes to push notifications when user authenticates.
 *
 * This hook checks for push notification support and permission,
 * then subscribes to browser push notifications and stores the
 * subscription on the server.
 *
 * @param user - The authenticated user object (must be passed from parent component to avoid context issues)
 * @param options - Configuration options
 * @param options.requestPermissionIfDefault - Whether to request permission if it's 'default'
 *
 * @returns Object containing subscription state
 *
 * @example
 * ```tsx
 * useAutoPushSubscription(user, { requestPermissionIfDefault: true })
 * ```
 */
export function useAutoPushSubscription(
  user?: { id: string; email: string; name: string } | null,
  options?: UseAutoPushSubscriptionOptions,
) {
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const { requestPermissionIfDefault = false } = options || {};

  // Guard against multiple attempts — only try once per user ID
  const attemptedUserId = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!user) {
      setIsSubscribed(false);
      attemptedUserId.current = undefined;
      return;
    }

    // Already attempted for this user — don't retry
    if (attemptedUserId.current === user.id) {
      return;
    }
    attemptedUserId.current = user.id;

    const checkAndSubscribe = async () => {
      // Check if push notifications are supported
      if (!isPushNotificationSupported()) {
        return;
      }

      // Get current permission state
      let permission = Notification.permission;

      // Request permission if configured and permission is 'default'
      if (permission === 'default' && requestPermissionIfDefault) {
        permission = await requestNotificationPermission();
      }

      // Only proceed if permission is granted
      if (permission !== 'granted') {
        return;
      }

      // Ensure service worker is ready
      try {
        await navigator.serviceWorker.ready;
      } catch {
        return;
      }

      setIsSubscribing(true);

      try {
        // Check if already subscribed
        const existingSubscription = await getPushSubscription();
        if (existingSubscription) {
          setIsSubscribed(true);
          return;
        }

        // Fetch VAPID public key from server
        let vapidResponse: { publicKey: string };
        try {
          vapidResponse = await usersApi.getVAPIDPublicKey();
        } catch {
          // VAPID key not available — push notifications not configured, skip silently
          return;
        }
        const vapidPublicKey = vapidResponse.publicKey;

        if (!vapidPublicKey) {
          return;
        }

        // Subscribe to push notifications (no retry — failures aren't recoverable)
        const subscription = await subscribeToPushNotifications(vapidPublicKey);

        if (!subscription) {
          return;
        }

        // Serialize and store subscription on server
        const subscriptionData = serializePushSubscription(subscription);
        await usersApi.subscribeToPush(user.id, subscriptionData);

        setIsSubscribed(true);
        toast.success('Push notifications enabled');
      } catch {
        // Don't block auth flow, just swallow the error
      } finally {
        setIsSubscribing(false);
      }
    };

    checkAndSubscribe().catch(() => {
      // Silently ignore
    });
    // Only depend on user.id to avoid re-running on object reference changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, requestPermissionIfDefault]);

  return { isSubscribing, isSubscribed };
}

export type { UseAutoPushSubscriptionOptions };
